import React, { useEffect } from 'react';
import { X, Printer, Receipt, User, Calendar, CreditCard, FileText, CheckCircle, Infinity, Clock, Package, Tag } from 'lucide-react';

interface ReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchase: {
    id: string;
    planName: string;
    planPrice: number;
    paymentId: string;
    createdAt: Date;
    status: string;
    expiryDate?: Date | null;
    isLifetime?: boolean;
    discountCode?: string;
    originalPrice?: number;
    isRenewal?: boolean;
    items?: any[];
    fullName?: string;
    buyerName?: string;
    source?: 'coursedee_cart' | 'salepage' | 'subscription';
  };
  userEmail?: string;
  userName?: string;
}

const ReceiptModal: React.FC<ReceiptModalProps> = ({ isOpen, onClose, purchase, userEmail, userName }) => {
  // Debug logging
  useEffect(() => {
    if (isOpen) {
      console.log('[ReceiptModal] Purchase data:', purchase);
      console.log('[ReceiptModal] fullName:', purchase.fullName);
      console.log('[ReceiptModal] buyerName:', purchase.buyerName);
      console.log('[ReceiptModal] userName prop:', userName);
      console.log('[ReceiptModal] discountCode:', purchase.discountCode);
      console.log('[ReceiptModal] originalPrice:', purchase.originalPrice);
      console.log('[ReceiptModal] planPrice:', purchase.planPrice);
    }
  }, [isOpen, purchase, userName]);

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
    }
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handlePrint = () => {
    window.print();
  };

  if (!isOpen) return null;

  const discount = purchase.originalPrice && purchase.originalPrice > purchase.planPrice 
    ? purchase.originalPrice - purchase.planPrice 
    : 0;

  // Determine source text
  const getSourceText = () => {
    switch (purchase.source) {
      case 'coursedee_cart':
        return 'AIForYou';
      case 'salepage':
        return 'saledi.web.app';
      case 'subscription':
        return 'edumi.co';
      default:
        return 'edumi.co';
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-600 text-white p-8 text-center relative print:bg-purple-600">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors print:hidden"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="flex items-center justify-center mb-3">
            <Receipt className="w-12 h-12" />
          </div>
          <h2 className="text-3xl font-bold">ใบเสร็จรับเงิน / Receipt</h2>
        </div>

        {/* Receipt Content */}
        <div className="p-8 space-y-6">
          {/* Receipt Info */}
          <div className="flex justify-between items-start pb-4 border-b-2 border-gray-200">
            <div>
              <p className="text-sm text-gray-500 font-medium">เลขที่ใบเสร็จ</p>
              <p className="text-lg font-bold text-gray-900">#PAY-{purchase.paymentId.substring(0, 10)}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500 font-medium">วันที่ออกใบเสร็จ</p>
              <p className="text-lg font-bold text-gray-900">
                {purchase.createdAt.toLocaleDateString('th-TH', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              <p className="text-sm text-gray-600">
                เวลา {purchase.createdAt.toLocaleTimeString('th-TH', {
                  hour: '2-digit',
                  minute: '2-digit'
                })} น.
              </p>
            </div>
          </div>

          {/* Customer Info */}
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-purple-600" />
              <h3 className="font-bold text-gray-900">ข้อมูลผู้ซื้อ</h3>
            </div>
            <div className="space-y-2">
              {(purchase.fullName || purchase.buyerName || userName) && (
                <div className="flex items-start">
                  <span className="text-sm text-gray-600 w-24">ชื่อ:</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {purchase.fullName || purchase.buyerName || userName}
                  </span>
                </div>
              )}
              {userEmail && (
                <div className="flex items-start">
                  <span className="text-sm text-gray-600 w-24">อีเมล:</span>
                  <span className="text-sm font-semibold text-gray-900">{userEmail}</span>
                </div>
              )}
            </div>
          </div>

          {/* Items */}
          <div className="border-t-2 border-b-2 border-gray-200 py-6">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-purple-600" />
              <h3 className="font-bold text-gray-900">รายการสินค้า</h3>
            </div>
            
            <div className="space-y-3">
              {/* Product Name */}
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <p className="font-bold text-gray-900 text-lg mb-2">{purchase.planName}</p>
                
                {/* Duration */}
                <div className="flex items-center gap-2 text-sm text-purple-700">
                  {purchase.isLifetime ? (
                    <>
                      <Infinity className="w-4 h-4" />
                      <span>ใช้งานได้: ไม่มีวันหมดอายุ</span>
                    </>
                  ) : purchase.expiryDate ? (
                    <>
                      <Clock className="w-4 h-4" />
                      <span>
                        ใช้งานได้ถึง: {purchase.expiryDate.toLocaleDateString('th-TH', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </>
                  ) : (
                    <>
                      <Infinity className="w-4 h-4" />
                      <span>ใช้งานได้: ไม่มีวันหมดอายุ</span>
                    </>
                  )}
                </div>
              </div>

              {/* Package Items (if multiple) */}
              {purchase.items && purchase.items.length > 0 && (
                <div className="ml-4 space-y-2">
                  {purchase.items.map((item: any, idx: number) => (
                    <div key={idx} className="flex justify-between text-sm text-gray-600">
                      <span>• {item.name}</span>
                      <span>฿{item.price?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Pricing */}
              <div className="space-y-2 pt-4">
                {purchase.originalPrice && purchase.originalPrice > purchase.planPrice ? (
                  <>
                    <div className="flex justify-between text-gray-600">
                      <span>ราคาเต็ม</span>
                      <span>฿{purchase.originalPrice.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-green-600 font-semibold">
                      <span>
                        ส่วนลด{purchase.discountCode ? ` (${purchase.discountCode})` : ''}
                      </span>
                      <span>-฿{discount.toLocaleString()}</span>
                    </div>
                  </>
                ) : null}
                
                <div className="flex justify-between text-xl font-bold text-purple-600 pt-3 border-t-2 border-gray-300">
                  <span>รวมทั้งสิ้น</span>
                  <span>฿{purchase.planPrice.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Info - Only show for paid purchases */}
          {purchase.planPrice > 0 && (
            <div className="bg-green-50 rounded-xl p-6 border border-green-200">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-gray-900">ข้อมูลการชำระเงิน</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <span className="text-sm text-gray-600">ช่องทางชำระเงิน:</span>
                  <span className="text-sm font-semibold text-gray-900">โอนเงินผ่านธนาคาร</span>
                </div>
                <div className="flex items-center gap-2 text-green-600 font-semibold">
                  <CheckCircle className="w-5 h-5" />
                  <span>ชำระเงินเรียบร้อยแล้ว</span>
                </div>
                <div className="flex items-start justify-between pt-3 border-t border-green-200">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-600" />
                    <span className="text-sm text-gray-600">Payment ID:</span>
                  </div>
                  <span className="text-sm font-mono font-semibold text-gray-900 break-all text-right ml-2">
                    {purchase.paymentId}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {/* Free Purchase Info - Show for 100% discount */}
          {purchase.planPrice === 0 && (
            <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
              <div className="flex items-center gap-2 mb-4">
                <Tag className="w-5 h-5 text-purple-600" />
                <h3 className="font-bold text-gray-900">รหัสส่วนลด</h3>
              </div>
              <div className="space-y-3">
                {purchase.discountCode && (
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-600">โค้ดส่วนลดที่ใช้:</span>
                    <span className="text-sm font-bold text-purple-600">{purchase.discountCode}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-green-600 font-semibold">
                  <CheckCircle className="w-5 h-5" />
                  <span>สำเร็จเรียบร้อยแล้ว (ไม่มีค่าใช้จ่าย)</span>
                </div>
              </div>
            </div>
          )}

          {/* Discount Code (only show for paid purchases with discount) */}
          {purchase.planPrice > 0 && purchase.discountCode && (
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-6 border border-amber-200">
              <div className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="text-sm text-amber-700 font-medium">โค้ดส่วนลดที่ใช้</p>
                  <p className="text-lg font-bold text-amber-900">{purchase.discountCode}</p>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center pt-6 border-t-2 border-gray-200 space-y-3">
            <p className="text-gray-900 font-bold text-lg">ขอบคุณที่ใช้บริการ</p>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-sm text-gray-600 mb-1">ซื้อผ่าน:</p>
              <p className="text-lg font-bold text-purple-600">{getSourceText()}</p>
            </div>
            <div className="pt-2">
              <p className="text-sm text-gray-600">จัดทำโดย: <span className="font-semibold text-gray-900">EduMi Platform</span></p>
              <p className="text-sm text-gray-600">เว็บไซต์: <span className="font-semibold text-purple-600">edumi.co</span></p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 print:hidden">
            <button
              onClick={handlePrint}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <Printer className="w-5 h-5" />
              พิมพ์ใบเสร็จ
            </button>
            <button
              onClick={onClose}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all duration-300"
            >
              <X className="w-5 h-5" />
              ปิด
            </button>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          /* Force single page printing */
          @page {
            margin: 0.5cm;
            size: A4 portrait;
          }
          
          /* Prevent page breaks and orphans/widows */
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            height: 100% !important;
            page-break-after: avoid !important;
            page-break-before: avoid !important;
            page-break-inside: avoid !important;
            orphans: 0 !important;
            widows: 0 !important;
          }
          
          /* Hide everything by default */
          body * {
            visibility: hidden;
            page-break-inside: avoid !important;
            page-break-after: avoid !important;
            page-break-before: avoid !important;
          }
          
          /* Completely hide modal backdrop/overlay */
          .fixed.inset-0 {
            position: static !important;
            background: transparent !important;
            background-color: transparent !important;
            inset: auto !important;
            z-index: auto !important;
            display: block !important;
            padding: 0 !important;
            margin: 0 !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
          }
          
          /* Hide backdrop pseudo-elements */
          .fixed.inset-0::before,
          .fixed.inset-0::after {
            display: none !important;
          }
          
          /* Show only the receipt content */
          .fixed.inset-0 *,
          .fixed.inset-0 {
            visibility: visible;
          }
          
          /* Hide close button and action buttons */
          .print\\:hidden {
            display: none !important;
            visibility: hidden !important;
          }
          
          /* Receipt container - force single page */
          .fixed.inset-0 > div {
            max-width: 100% !important;
            width: 100% !important;
            margin: 0 auto !important;
            padding: 0 !important;
            box-shadow: none !important;
            max-height: 100vh !important;
            overflow: hidden !important;
            transform: scale(0.92) !important;
            transform-origin: top center !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
            page-break-before: avoid !important;
            border-radius: 0 !important;
          }
          
          /* Force content to fit on one page */
          .fixed.inset-0 > div > * {
            page-break-inside: avoid !important;
            page-break-after: avoid !important;
          }
          
          /* Hide scrollbars completely */
          * {
            overflow: hidden !important;
            -ms-overflow-style: none !important;
            scrollbar-width: none !important;
          }
          
          *::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
          
          /* Reduce padding to fit on one page */
          .p-8 {
            padding: 0.75rem !important;
          }
          
          .p-6 {
            padding: 0.5rem !important;
          }
          
          .space-y-6 > * + * {
            margin-top: 0.5rem !important;
          }
          
          .space-y-3 > * + * {
            margin-top: 0.35rem !important;
          }
          
          .space-y-2 > * + * {
            margin-top: 0.25rem !important;
          }
          
          /* Reduce header padding */
          .bg-gradient-to-r.from-purple-600 {
            padding: 1rem !important;
          }
          
          /* Preserve gradient colors */
          [class*="bg-gradient"] {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          /* Preserve all background colors */
          * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          /* Force no page breaks on all elements */
          div, section, article, p, h1, h2, h3, h4, h5, h6 {
            page-break-inside: avoid !important;
            page-break-after: avoid !important;
            page-break-before: avoid !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ReceiptModal;
